import { useEffect } from "react";

import { useAppSelector } from "@/store/hooks";
import instance from "@/store/middleware/api";
import { isAuthorized } from "@/store/selectors/getMeSelector";

/**
 * Presence heartbeat — pings `POST /presence/heartbeat` every ~60s while the
 * user is logged in AND the tab is visible, plus once whenever the tab becomes
 * visible. Bumps `users.lastSeenAt`, which the backend's "After-inactivity" email
 * policy reads to decide whether to hold an email or send it. Mounted once in the
 * app shell (SecondHeader). When the user leaves/closes/backgrounds the tab the
 * pings stop, so they go inactive after the server's threshold (~5 min) and any
 * held emails release. See docu/flows/notifications/send-timing-policy.md.
 */
const HEARTBEAT_INTERVAL_MS = 60 * 1000;

export const usePresenceHeartbeat = (): void => {
  const loggedIn = useAppSelector(isAuthorized);

  useEffect(() => {
    if (!loggedIn) return;

    const ping = (): void => {
      if (document.visibilityState !== "visible") return;
      instance.post("/presence/heartbeat").catch(() => {
        // Best-effort — a missed beat just means the user looks idle a little
        // sooner; the next beat (or page) recovers it.
      });
    };

    ping(); // immediate on login / mount
    const timer = window.setInterval(ping, HEARTBEAT_INTERVAL_MS);
    const onVisible = (): void => {
      if (document.visibilityState === "visible") ping();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loggedIn]);
};
